import { Injectable, signal, WritableSignal } from '@angular/core';
import { scaleLinear } from 'd3-scale';

import { MOBILE_BREAKPOINT } from '@config';

import { Content } from '@23degrees/shared-code/models';
import { roundTo } from '@23degrees/shared-code/utilities';

@Injectable({
  providedIn: 'root',
})
export class BreakPointManager {
  private sizeScale = scaleLinear().clamp(true);
  private contentMap: { [key: string]: { content: Content; sizeFactor: WritableSignal<number>; slug: string; nativeElement: HTMLElement } } = {};

  public setContent(msg) {
    const { content } = msg;

    if (this.contentMap[content.slug]) {
      this.contentMap[content.slug].content = content;
    }

    this.updateSizeFactor();
  }

  private calcSizeFactor = (slug, width) => {
    if (!this.contentMap[slug]) return 1;
    if (!this.contentMap[slug].content) return 1;
    if (!this.contentMap[slug].content.typeSpecific) return 1;

    const { breakPoints } = this.contentMap[slug].content.typeSpecific.style;
    if (!breakPoints.enabled) return 1;

    const domain = breakPoints.values.map((obj) => obj.domain);
    const range = breakPoints.values.map((obj) => obj.range);

    this.sizeScale.domain(domain).range(range);
    return roundTo(this.sizeScale(width));
  };

  // called from setContainerRef / window resize
  public updateSizeFactor = (msg?) => {
    // this.cleanContainerMap();

    for (const slug of Object.keys(this.contentMap)) {
      // if (msg && msg.slug !== slug) continue;
      const { nativeElement } = this.contentMap[slug];

      const width = nativeElement.offsetWidth;
      // if (msg && msg.width) width = msg.width;

      this.contentMap[slug].sizeFactor.set(this.calcSizeFactor(slug, width));
    }
  };

  private cleanContainerMap = () => {
    for (const slug of Object.keys(this.contentMap)) {
      const { nativeElement } = this.contentMap[slug];
      if (nativeElement.offsetWidth === 0 && nativeElement.offsetHeight === 0) {
        delete this.contentMap[slug];
      }
    }
  };

  // called from content-selectr?
  public setContainerRef = (element: HTMLElement, content: Content, width?: number) => {
    const { slug } = content;
    this.cleanContainerMap();
    this.contentMap[slug] = { content, sizeFactor: signal(1), slug: slug, nativeElement: element };
    this.contentMap[slug].sizeFactor.set(this.calcSizeFactor(slug, width ?? element.offsetWidth));
  };

  // called from chart
  public getContainerElement = (slug) => {
    if (!this.contentMap[slug]) return;
    return this.contentMap[slug].nativeElement;
  };

  // called from style-service?
  public getSizeFactor = (slug, width?) => {
    if (width) return this.calcSizeFactor(slug, width);
    if (!this.contentMap[slug]) return 1;
    return this.contentMap[slug].sizeFactor();
  };

  public getSizeFactorSignal = (slug, width?) => {
    return this.contentMap[slug]?.sizeFactor ?? signal(1);
  };

  public getWidth = (slug) => {
    if (!this.contentMap[slug]) return -1;
    return this.contentMap[slug].nativeElement.offsetWidth;
  };

  public getHeight = (slug) => {
    if (!this.contentMap[slug]) return -1;
    return this.contentMap[slug].nativeElement.offsetHeight;
  };

  // am i mobile or not?
  public isMobile = (slug, width?) => {
    if (!this.contentMap[slug]) return false;
    if (width) return width <= MOBILE_BREAKPOINT;
    return this.contentMap[slug].nativeElement.offsetWidth <= MOBILE_BREAKPOINT;
  };

  // Used to get natural height based on c-item breakpoints
  public calcSizeFromBreakPts = (breakPoints: any, width: number): number => {
    if (!breakPoints.enabled) return 1;

    const domain = breakPoints.values.map((obj) => obj.domain);
    const range = breakPoints.values.map((obj) => obj.range);

    this.sizeScale.domain(domain).range(range);
    return roundTo(this.sizeScale(width));
  };
}
